import React from "react";
import { DocumentData, WithFieldValue } from "firebase/firestore";
import { Alert, ButtonProps, Text } from "@chakra-ui/react";

import { ButtonModal, JsonForm, JsonFormControl } from "@components";
import { useAuth, useCustomToast, useUploadImage } from "@hooks";

type AddImageModalProps = ButtonProps & {
  itemId: string;
};

const AddImageFormFields: JsonFormControl[] = [
  {
    label: "Title",
    inputType: "text",
    fieldName: "title",
    placeholder: "Title",
    defaultValue: "",
    config: {
      required: "Title is required",
    },
  },
  {
    label: "Image",
    inputType: "image",
    fieldName: "image",
    defaultValue: undefined,
    config: {
      required: "Image is required",
    },
  },
  {
    label: "Comments",
    inputType: "text",
    fieldName: "comments",
    defaultValue: "",
    placeholder: "Comments",
  },
];

export function AddImageModal({ itemId, ...delegated }: AddImageModalProps) {
  const authData = useAuth();
  const imageAddedToast = useCustomToast(() => (
    <Alert status="success">Image Added!</Alert>
  ));
  const [error, setError] = React.useState("");
  const upload = useUploadImage();

  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      setError("");
      try {
        if (data.image) {
          await upload(data.image, {
            title: data.title,
            comments: data.comments,
            module: itemId,
            userId: authData.user?.firebaseUser?.uid || "",
            userName:
              authData.user?.firstName && authData.user?.lastName
                ? `${authData.user.firstName} ${authData.user.lastName}`
                : "",
          });
        }
        imageAddedToast();
      } catch (ex) {
        setError((ex as Error).message || "");
      }
    },
    [itemId],
  );

  return (
    <ButtonModal label="Add Image" title="Add Image" size="sm" {...delegated}>
      <JsonForm
        fields={AddImageFormFields}
        onSubmit={doSubmit}
        clearAfterSubmit
      />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
